import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  AdoptionBulkResult,
  AdoptionCounts,
  AdoptionDetail,
  AdoptionListFilters,
  AdoptionListResponse,
  AdoptionModerationAction,
  AdoptionParticipant,
  AdoptionRefundAllResult,
  AdoptionRefundView,
} from './types';

export async function fetch_adoption_list(
  filters: AdoptionListFilters,
  sort?: SortState,
): Promise<AdoptionListResponse> {
  const res = await api.get<AdoptionListResponse>('/admin/adoption/listings', {
    params: build_params(filters as Record<string, never>, sort),
  });
  return res.data;
}

export async function fetch_adoption_counts(): Promise<AdoptionCounts> {
  const res = await api.get<AdoptionCounts>('/admin/adoption/listings/counts');
  return res.data;
}

export async function fetch_adoption_detail(id: string): Promise<AdoptionDetail> {
  const res = await api.get<AdoptionDetail>(`/admin/adoption/listings/${id}`);
  return res.data;
}

/**
 * Hide, restore or remove one listing.
 *
 * The reason is mandatory server-side and is repeated to the poster verbatim.
 * These are listings put up by ordinary people about their own animals, so one
 * disappearing from the feed unexplained is the most support-generating thing
 * this console can do.
 */
export async function moderate_listing(
  id: string,
  action: AdoptionModerationAction,
  reason: string,
): Promise<AdoptionDetail> {
  const res = await api.post<AdoptionDetail>(`/admin/adoption/listings/${id}/${action}`, {
    reason,
  });
  return res.data;
}

/** The same decision across a selection — the one-person-posted-this-eleven-times case. */
export async function bulk_moderate_listings(
  listing_ids: string[],
  action: AdoptionModerationAction,
  reason: string,
): Promise<AdoptionBulkResult> {
  const res = await api.post<AdoptionBulkResult>('/admin/adoption/listings/bulk', {
    listing_ids,
    action,
    reason,
  });
  return res.data;
}

export async function export_adoption_listings(
  filters: AdoptionListFilters,
  sort?: SortState,
): Promise<void> {
  await download_export(
    '/admin/adoption/listings/export',
    build_params(filters as Record<string, never>, sort),
    'adoption-listings.csv',
  );
}

/**
 * Who paid on this listing, who took the pet, and who is owed their fee back.
 *
 * A separate call from the listing detail: it joins payments and refund state,
 * which an admin reading a description has no use for.
 */
export async function fetch_adoption_refunds(id: string): Promise<AdoptionRefundView> {
  const res = await api.get<AdoptionRefundView>(`/admin/adoption/listings/${id}/refunds`);
  return res.data;
}

/** Record which paid enquirer took the pet. Pass null to clear it. */
export async function set_adoption_adopter(
  id: string,
  request_id: string | null,
): Promise<AdoptionRefundView> {
  const res = await api.post<AdoptionRefundView>(`/admin/adoption/listings/${id}/adopter`, {
    request_id,
  });
  return res.data;
}

export async function refund_adoption_participant(
  id: string,
  request_id: string,
): Promise<AdoptionRefundView> {
  const res = await api.post<AdoptionRefundView>(
    `/admin/adoption/listings/${id}/refunds/${request_id}`,
    {},
  );
  return res.data;
}

/** Everyone eligible at once. Reports per-person failures rather than failing the batch. */
export async function refund_all_adoption_participants(
  id: string,
): Promise<AdoptionRefundAllResult> {
  const res = await api.post<AdoptionRefundAllResult>(
    `/admin/adoption/listings/${id}/refunds`,
    {},
  );
  return res.data;
}

export type { AdoptionParticipant };
